import { useCallback, useEffect, useRef, useState } from "react"
import FolderIcon from "../../../../media/FolderIcon"
import { AngularScopeQRWorkflow, CladdingMaterial, InsulationMaterial, PatchScopeQRWorkflow, ScopeQRWorkflow, WorkflowLocation } from "../../../../types/types"
import { AngularScopeWorkflow, PatchScopeWorkflow, Workflow } from "../../../../types/starAPIResponseTypes"
import { FileExplorerDataProps } from "../../WorkflowFileExplorer.types"
import ScopeWorkflowsSection from "./ScopeWorkflows"
import ScopeWorkflowFormType from "../../../../utils/ScopeWorkflowFormType"
import { getCladdingMaterials } from "../../../../service/claddingMaterialService"
import { getInsulationMaterials } from "../../../../service/insulationMaterialService"
import { getLocations } from "../../../../service/workflowLocationService"
import SkeletonLoadingView from "../../../../components/SkeletonLoadingView/SkeletonLoadingView"
import { showErrorToast, showLoadingToast, showSuccessToast } from "../../../../utils/utils"
import QRIcon from "../../../../media/QRIcon"
import { MAX_QR_CODE_CHARS } from "../../../../utils/constants"
import { get } from "http"
import { Id, toast } from "react-toastify"
import { failedToBuildHeaders } from "@azure/msal-browser/dist/error/BrowserAuthErrorCodes"
import { addWorkflow, deleteWorkflow, getWorkflow, getWorkflows, getWorkflowsForIds, updateWorkflow } from "../../../../service/workflowService"
import WorkflowType from "../../../../enums/workflowType"
import { STARAPIError } from "../../../../service/service"
import useClickOutside from "../../../../hooks/useClickOutside"

const useScopeWorkflowsSection = (props: FileExplorerDataProps) => {
  const { limit, skip, onError, searchQuery } = props

  const [workflows, setWorkflows] = useState<Workflow[]>([])
  const [claddingMaterials, setCladdingMaterials] = useState<string[]>([])
  const [insulationMaterials, setInsulationMaterials] = useState<string[]>([])
  const [locations, setLocations] = useState<string[]>([])
  const [selectedWorkflows, setSelectedWorkflows] = useState<number[]>([])
  const [selectedWorkflow, setSelectedWorkflow] = useState<Workflow|undefined>(undefined)
  const [showQRCode, setShowQRCode] = useState<boolean>(false)
  const [showAddAngularScopeWorkflow, setShowAddAngularScopeWorkflow] = useState<boolean>(false)
  const [showAddPatchScopeWorkflow, setShowAddPatchScopeWorkflow] = useState<boolean>(false)
  const [showSelectWorkflowType, setShowSelectWorkflowType] = useState<boolean>(false)
  const [isSavingWorkflow, setIsSavingWorkflow] = useState<boolean>(false)
  const [selectedFormType, setSelectedFormType] = useState<ScopeWorkflowFormType>(ScopeWorkflowFormType.ADD)
  const [scopeQRString, setScopeQRString] = useState<string>("")
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [loadingToastId, setLoadingToastId] = useState<Id|undefined>(undefined)
  const [error, setError] = useState<Error|undefined>(undefined)
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<boolean>(false)
  const [canLoadMore, setCanLoadMore] = useState<boolean>(true)

  const [selectScopeTypeX, setSelectScopeTypeX] = useState<number | null>(null)
  const [selectScopeTypeY, setSelectScopeTypeY] = useState<number | null>(null)

  const [{
    contextRef: selectScopeMenuRef
  }] = useClickOutside({
    onClickOutside: () => setShowSelectWorkflowType(false)
  })

  useEffect(() => {
    if (!selectScopeTypeX) return
    if (!selectScopeTypeY) return
    const handleClickOutside = (e: MouseEvent) => {
      if (selectScopeMenuRef.current && !selectScopeMenuRef.current.contains(e.target as Node)) {
        setShowSelectWorkflowType(false)
      }
    };

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [selectScopeMenuRef])

  useEffect(() => {
    if (!isLoading && loadingToastId !== undefined) {
      toast.done(loadingToastId)
    }
  }, [isLoading, loadingToastId])

  const checkError = useCallback((response: any) => {
    setIsSavingWorkflow(false)
    if (response instanceof Error) {      
      setIsLoading(false)
      onError(response)
      setError(response)
      setCanLoadMore(false)
      return true
    }
    return false
  }, [onError])

  const onRefresh = useCallback(async () => {
    setIsLoading(true)
    const workflowResponse = await getWorkflows(100, 0, [WorkflowType.ANGULAR_SCOPE, WorkflowType.PATCH_SCOPE])
    if (checkError(workflowResponse)) { return }
    const fetchedWorkflows = workflowResponse as Workflow[]
    setWorkflows(fetchedWorkflows)
    setCanLoadMore(fetchedWorkflows.length >= limit)

    const claddingMaterialResponse = await getCladdingMaterials()
    if (checkError(claddingMaterialResponse)) { return }
    const cladding = claddingMaterialResponse as CladdingMaterial[]
    setCladdingMaterials(cladding.map((material) => material.material))

    const insulationMaterialResponse = await getInsulationMaterials()
    if (checkError(insulationMaterialResponse)) { return }
    const insulation = insulationMaterialResponse as InsulationMaterial[]
    setInsulationMaterials(insulation.map((material) => material.material))

    const locationResponse = await getLocations()
    if (checkError(locationResponse)) { return }
    const workflowLocations = locationResponse as WorkflowLocation[]
    setLocations(workflowLocations.map((location) => location.name))

    setIsLoading(false)
  }, [limit, skip, checkError])

  useEffect(() => {
    onRefresh()
  }, [onRefresh])

  const onCheckWorkflow = async (index: number) => {
    if (selectedWorkflows.includes(index)) {
      const selected = selectedWorkflows.filter((i) => i !== index)
      const workflowString = await getQRCodeString(selected)
      setScopeQRString(workflowString)
      setSelectedWorkflows(selected)
      console.log(`result after removed: ${workflowString}`)
    } else {
      const selected = [...selectedWorkflows, ...[index]]
      const workflowString = await getQRCodeString(selected)
      console.log(`result after selecting: ${workflowString}`)
      if (workflowString.length > MAX_QR_CODE_CHARS) { 
        showErrorToast("Maximum QR code length exceeded. Please select fewer workflows.")
        return
      }
      setScopeQRString(workflowString)
      setSelectedWorkflows([...selectedWorkflows, ...[index]])
    }
  }

  const getQRCodeString = async (workflowIndexs: number[]) => {    
    const workflowsToGenerate = workflows.filter((_, index) => workflowIndexs.includes(index))
    const workflowIds = workflowsToGenerate.map((workflow) => workflow.workflow_id)
    const workflowResponse = await getWorkflowsForIds(workflowIds)

    if (workflowResponse instanceof Error) {
      console.log(workflowResponse)
      onError(workflowResponse)
      showErrorToast(`Failed to fetch workflows for QR code generation.`)
      return ""
    }
    const scopes = workflowResponse as Workflow[]
    
    return JSON.stringify(scopes.map((workflow) => {
      switch (workflow.workflow_type) {
        case WorkflowType.ANGULAR_SCOPE:
          const angularScope = workflow as AngularScopeWorkflow
          console.log(angularScope);
          
          const qr: AngularScopeQRWorkflow = {
            id: angularScope.asset_id,
            f: angularScope.filename,
            t: angularScope.asset_temp,
            d: angularScope.asset_diameter,
            l: angularScope.location,
            ln: angularScope.asset_length,
            s: angularScope.workflow_start,
            e: angularScope.workflow_end,
            tp: angularScope.asset_type,
            im: angularScope.insulation_material,
            it: angularScope.insulation_thickness,
            cm: angularScope.cladding_material,
            ct: angularScope.cladding_thickness,
            st: JSON.parse(angularScope.sections),
            ag: JSON.parse(angularScope.angles),
            tc: angularScope.has_tracing_cable ? 1 : 0
          }
          return qr
        case WorkflowType.PATCH_SCOPE:
          const patchScope = workflow as PatchScopeWorkflow
          const patchQR: PatchScopeQRWorkflow = {
            id: patchScope.asset_id,
            l: patchScope.location,
            f: patchScope.filename,
            t: patchScope.asset_temp,          
            tp: patchScope.asset_type,
            im: patchScope.insulation_material,
            it: patchScope.insulation_thickness,
            cm: patchScope.cladding_material,
            ct: patchScope.cladding_thickness,
            st: JSON.parse(patchScope.sections),
            p: JSON.parse(patchScope.horizontal_positions),
          }
          return patchQR
      }
    }))
  }

  const openScopeWorkflow = (formType: ScopeWorkflowFormType, workflow?: Workflow) => {    
    setSelectedFormType(formType)
    setSelectedWorkflow(workflow)

    if (!workflow) { return }
    if (workflow.workflow_type === WorkflowType.ANGULAR_SCOPE) {
      setShowAddAngularScopeWorkflow(true)
    }
    if (workflow.workflow_type === WorkflowType.PATCH_SCOPE) {
      setShowAddPatchScopeWorkflow(true)
    }
  }

  const onSubmitScopeWorkflowForm = (workflowObject: Workflow) => {
    if (selectedFormType === ScopeWorkflowFormType.ADD) {
      onAddWorkflow(workflowObject)
      return
    }
    if (selectedFormType === ScopeWorkflowFormType.EDIT) {
      onEditWorkflow(workflowObject)
      return
    }
  }

  const onAddWorkflow = async (workflowObject: Workflow) => {
    setIsSavingWorkflow(true)
    setIsLoading(true)
    setLoadingToastId(showLoadingToast(`Saving ${workflowObject.filename}.`))
    workflowObject["client_upload_uuid"] = `${crypto.randomUUID()}`
    const workflowResponse = await addWorkflow(workflowObject)
    setIsLoading(false)
    if (checkError(workflowResponse)) { return }

    setIsSavingWorkflow(false)
    setShowAddAngularScopeWorkflow(false)
    setShowAddPatchScopeWorkflow(false)
    setShowSelectWorkflowType(false)
    setSelectedWorkflows([])
    showSuccessToast(`${workflowObject.filename} created successfully.`)
    onRefresh()
  }

  const onDuplicateWorkflow = async (workflowObject: Workflow) => {
    setIsSavingWorkflow(true)
    const fullWorkflowResponse = await getWorkflow(workflowObject.workflow_id)
    if (checkError(fullWorkflowResponse)) { return }

    const fullWorkflow = fullWorkflowResponse as Workflow
    fullWorkflow["client_upload_uuid"] = `${crypto.randomUUID()}`
    fullWorkflow["filename"] = `${workflowObject.filename === "" ? workflowObject.asset_id : workflowObject.filename} (copy)`
    fullWorkflow["created_at"] = `${Math.floor(Date.now() / 1000)}`

    setIsLoading(true)
    setLoadingToastId(showLoadingToast(`Duplicating ${workflowObject.filename}.`))
    const workflowResponse = await addWorkflow(fullWorkflow)

    setIsLoading(false)
    if (checkError(workflowResponse)) { return }

    setIsSavingWorkflow(false)
    setShowAddAngularScopeWorkflow(false)
    setShowAddPatchScopeWorkflow(false)
    setShowSelectWorkflowType(false)
    setSelectedWorkflows([])
    showSuccessToast(`${workflowObject.filename} duplicated successfully.`)
    onRefresh()
  }

  const onEditWorkflow = async (workflowObject: Workflow) => {
    setIsSavingWorkflow(true)
    setIsLoading(true)
    setLoadingToastId(showLoadingToast(`Updating ${workflowObject.filename}.`))    
    const workflowResponse = await updateWorkflow(workflowObject.workflow_id, workflowObject)
    setIsLoading(false)
    if (checkError(workflowResponse)) { return }

    setIsSavingWorkflow(false)
    setShowAddAngularScopeWorkflow(false)
    setShowAddPatchScopeWorkflow(false)
    setShowSelectWorkflowType(false)
    setSelectedWorkflows([])
    showSuccessToast(`${workflowObject.filename} updated successfully.`)
    onRefresh()
  }

  const onDeleteWorkflow = async () => {
    setShowDeleteConfirmation(false)
    if (selectedWorkflow === undefined) {
      showErrorToast("Failed to delete workflow.")
      return
    }
    setIsLoading(true)
    setLoadingToastId(showLoadingToast(`Deleting ${selectedWorkflow.asset_id}.`))
    const response = await deleteWorkflow(selectedWorkflow.workflow_id)
    setIsLoading(false)

    if (checkError(response)) { return }
    showSuccessToast(`${selectedWorkflow.asset_id} deleted successfully.`)
    
    onRefresh()
  }

  const onAddAngularWorkflowScope = () => {
    setShowAddPatchScopeWorkflow(false)
    setShowSelectWorkflowType(false)
    setShowAddAngularScopeWorkflow(true)
    openScopeWorkflow(ScopeWorkflowFormType.ADD)
  }

  const onAddPatchWorkflowScope = () => {
    setShowAddAngularScopeWorkflow(false)
    setShowSelectWorkflowType(false)
    setShowAddPatchScopeWorkflow(true)
    openScopeWorkflow(ScopeWorkflowFormType.ADD)
  }

  const openSelectWorkflowTypeMenu = (e: React.MouseEvent) => {
    setShowSelectWorkflowType(true)
    setSelectScopeTypeX(e.clientX)
    setSelectScopeTypeY(e.clientY)
  }

  const onShowScopeWorkflowForm = () => {
    if (claddingMaterials.length === 0) {
      onError(new Error("Failed to fetch cladding materials. Cannot create scope workflow."))
      return
    }

    if (insulationMaterials.length === 0) {
      onError(new Error("Failed to fetch insulation materials. Cannot create scope workflow."))
      return
    }

    if (locations.length === 0) {
      onError(new Error("No locations found. Please add locations before creating a scope workflow."))
      return
    }
    openScopeWorkflow(ScopeWorkflowFormType.ADD)
  }

  const onShowQR = () => {
    if (selectedWorkflows.length === 0) {
      showErrorToast("No scope workflow selected")
    }
    setShowQRCode(selectedWorkflows.length > 0)
  }

  const canGenerateQRCode = (index: number) => {
    // const selected = [...selectedWorkflows, ...[index]]
    // const workflowString = await getQRCodeString(selected)
    // return workflowString.length < MAX_QR_CODE_CHARS
    return true
  }

  return [{
    isSavingWorkflow,
    fileExplorerOption: {
      option: {
        icon: <FolderIcon />,
        label: "Scope Workflows",
        onAdd: (e: React.MouseEvent) => openSelectWorkflowTypeMenu(e),
        onAddLabel: "Create workflow scope"
      },
      topBarItem: {
        icon: <div className="qr-icon"><QRIcon /></div>,
        onClick: onShowQR
      },
      component: <ScopeWorkflowsSection
                    workflows={workflows.filter((workflow) => workflow.asset_id.toLowerCase().includes(searchQuery.toLowerCase()))}
                    claddingMaterials={claddingMaterials}
                    insulationMaterials={insulationMaterials}
                    locations={locations}
                    selectedWorkflows={selectedWorkflows}
                    showQRCode={showQRCode}
                    showAddAngularScopeWorkflow={showAddAngularScopeWorkflow}
                    showAddPatchScopeWorkflow={showAddPatchScopeWorkflow}
                    showSelectWorkflowType={showSelectWorkflowType}
                    selectScopeTypeX={selectScopeTypeX ?? 0}
                    selectScopeTypeY={selectScopeTypeY ?? 0}
                    selectedWorkflow={selectedWorkflow}
                    selectScopeMenuRef={selectScopeMenuRef}
                    onHideSelectWorkflowType={() => setShowSelectWorkflowType(false)}
                    formType={selectedFormType}
                    onAddAngularWorkflowScope={onAddAngularWorkflowScope}
                    onAddPatchWorkflowScope={onAddPatchWorkflowScope}
                    onError={onError}
                    scopeQRString={scopeQRString}
                    onHideScopeWorkflow={() => {
                      setShowAddAngularScopeWorkflow(false)
                      setShowAddPatchScopeWorkflow(false)
                    }}
                    onHideQRCode={() => setShowQRCode(false)}
                    onCheckWorkflow={onCheckWorkflow}
                    onOpenWorkflow={openScopeWorkflow}
                    onSubmit={onSubmitScopeWorkflowForm}
                    onDeleteWorkflow={onDeleteWorkflow}
                    error={error}
                    onSelectWorkflow={setSelectedWorkflow}
                    showDeleteConfirmation={showDeleteConfirmation}
                    onDuplicateWorkflow={onDuplicateWorkflow}
                    onShowDeleteConfirmation={setShowDeleteConfirmation}
                    canGenerateQRCode={canGenerateQRCode}
                  />,
      onRefresh: onRefresh,
      filterOptions: <p>Filter</p>,
      sortOptions: <p>Sort</p>,
      canLoadMore: canLoadMore,
      isLoading: isLoading,
      loadingView: <>
        <div className="staged-workflows-header-container content-loading-view">
          <p className="workflows-result-header">ASSET ID</p>
          <p className="workflows-result-header">SECTIONS</p>
          <p className="workflows-result-header">DATE</p>
          <p className="workflows-result-header"></p>
          {[...Array(5)].map((_) => (
            <>
              <SkeletonLoadingView />
              <SkeletonLoadingView />
              <SkeletonLoadingView style={{gridColumn: "span 2"}} />
            </>
          ))}
        </div>
      </>
    }
  }]

}

export default useScopeWorkflowsSection